import React, { FC, useCallback, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import styled from 'styled-components';
import { Drawer, DrawerContent, DrawerTitle } from '../../ui/feedback/Drawer';
import { IconClose } from '../../ui/icon/IconClose';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { Image } from '../../ui/media/Image';
import { MediaCover } from '../../ui/media/MediaCover';
import { ProductsUI } from '../../ui/posts/types';
import { TransitionSlideUp } from '../../ui/transition/TransitionSlideUp';
import { Typography } from '../../ui/typography/Typography';
import { addProtocol } from '../../utils/addProtocol';
import { buildImageSource, buildImageSourceSet } from '../../utils/buildImageSource';
import { formatCurrency } from '../../utils/format/formatCurrency';
import { getIsMobile } from '../../utils/getIsMobile';
import { useTranslation } from '../../utils/useTranslation';
import { BusinessUI } from '../business/types';
import { IconForward } from '../icon/IconForward';
import { Button } from '../input/Button';
import { Flex } from '../layout/Flex';
import { Avatar } from '../media/Avatar';

interface GridContainerProps {
    isMobile: boolean;
}

const GridContainer = styled(Box).attrs((props) => ({
    ...props,
    display: 'grid',
}))<GridContainerProps>`
    row-gap: 10px;
    column-gap: 10px;
    grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr 1fr' : '1fr 1fr 1fr')};
`;

const Card = styled(Box).attrs((props) => ({
    ...props,
    as: 'a',
    target: '_blank',
    rel: 'noopener noreferrer',
    backgroundColor: 'var(--background-secondary)',
    overflow: 'hidden',
    borderRadius: 3,
    p: 0.5,
    textDecoration: 'none',
}))``;

const Title = styled(Typography).attrs((props) => ({
    ...props,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}))`
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

interface TrackProductClickOutProps {
    url?: string;
    target: string;
    productId?: string;
}

interface PostProductsDrawerProps {
    products: ProductsUI;
    business: BusinessUI;
    handleClose: VoidFunction;
    trackProductClickOut: (props: TrackProductClickOutProps) => void;
}

export const PostProductsDrawer: FC<PostProductsDrawerProps> = ({
    handleClose,
    products,
    business,
    trackProductClickOut,
}) => {
    const [step, setStep] = useState<'OPEN' | 'CLOSE'>('OPEN');
    const t = useTranslation();
    const isMobile = getIsMobile();

    const onClose = useCallback(() => {
        setStep('CLOSE');
        handleClose();
    }, [handleClose]);

    return (
        <SwitchTransition>
            <TransitionSlideUp key={step}>
                {products.items.length > 1 ? (
                    <Drawer onClose={onClose}>
                        <DrawerTitle
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            py={1}
                            px={2}
                        >
                            <Typography variant="title3" weight="bold">
                                {t(products.title)}
                            </Typography>
                            <Box>
                                <IconButton onClick={onClose}>
                                    <IconClose />
                                </IconButton>
                            </Box>
                        </DrawerTitle>
                        <DrawerContent p={2} pt={0}>
                            <GridContainer width="100%" isMobile={isMobile}>
                                {products.items.map(
                                    ({
                                        oldPriceInfo: {
                                            currency: oldCurrency,
                                            integerPart: oldIntegerPart,
                                            fractionalPart: oldFractionalPart,
                                        },
                                        priceInfo: { integerPart, fractionalPart },
                                        content: [{ media }],
                                        title,
                                        url,
                                        id,
                                    }) => (
                                        <Card
                                            href={addProtocol(url)}
                                            onClick={() =>
                                                trackProductClickOut({
                                                    url,
                                                    target: 'product',
                                                    productId: id,
                                                })
                                            }
                                        >
                                            <MediaCover
                                                ratio={1}
                                                overflow="hidden"
                                                borderRadius="20px"
                                            >
                                                <Flex
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    backgroundColor="white"
                                                >
                                                    <Image
                                                        objectFit="contain"
                                                        width="100%"
                                                        height="100%"
                                                        src={buildImageSource(media)}
                                                        srcSet={buildImageSourceSet(media)}
                                                    />
                                                </Flex>
                                            </MediaCover>
                                            <Box p={1}>
                                                <Title variant="body2" weight="regular">
                                                    {t(title)}
                                                </Title>
                                                <Flex direction="row" alignItems="baseline">
                                                    <Typography
                                                        weight="bold"
                                                        variant="body"
                                                        color="var(--control-primary-background)"
                                                        textTransform="none"
                                                    >
                                                        {formatCurrency(
                                                            integerPart,
                                                            fractionalPart,
                                                        )}
                                                    </Typography>
                                                    {oldCurrency && (
                                                        <Typography
                                                            variant="caption"
                                                            color="var(--control-disabled-color)"
                                                            textTransform="none"
                                                            textDecoration="line-through"
                                                            ml={1}
                                                        >
                                                            {formatCurrency(
                                                                oldIntegerPart,
                                                                oldFractionalPart,
                                                            )}
                                                        </Typography>
                                                    )}
                                                </Flex>
                                            </Box>
                                        </Card>
                                    ),
                                )}
                            </GridContainer>
                            {business.siteUrl && (
                                <Flex
                                    direction="row"
                                    justifyContent="space-between"
                                    width="100%"
                                    alignItems="center"
                                    py={2}
                                >
                                    <Flex
                                        as="a"
                                        href={business.siteUrl}
                                        target="_blank"
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                        textDecoration="none"
                                        onClick={() =>
                                            trackProductClickOut({
                                                url: business.siteUrl,
                                                target: 'product',
                                            })
                                        }
                                    >
                                        <Avatar
                                            width={34}
                                            title={business.displayName}
                                            src={business.avatarUrl}
                                            border="1px solid var(--divider)"
                                        />
                                        <Flex>
                                            <Typography>
                                                {products.businessButtonTitle || t('Еще товары')}
                                            </Typography>
                                            <Typography variant="caption" color="secondary">
                                                {business.shortSite}
                                            </Typography>
                                        </Flex>
                                    </Flex>
                                    <IconForward fontSize="16px" fill="var(--color-primary)" />
                                </Flex>
                            )}
                        </DrawerContent>
                    </Drawer>
                ) : (
                    <Drawer onClose={onClose}>
                        <DrawerContent p={0}>
                            <MediaCover
                                ratio={1}
                                overflow="hidden"
                                width="100%"
                                borderTopLeftRadius={3}
                                borderTopRightRadius={3}
                            >
                                <Flex
                                    alignItems="center"
                                    justifyContent="center"
                                    backgroundColor="white"
                                >
                                    <Image
                                        objectFit="contain"
                                        width="100%"
                                        height="100%"
                                        src={buildImageSource(products.items[0].content[0].media)}
                                        srcSet={buildImageSourceSet(
                                            products.items[0].content[0].media,
                                        )}
                                    />
                                </Flex>
                            </MediaCover>
                            <Box position="absolute" right={1.25} top={1.25}>
                                <IconButton
                                    variant="filled"
                                    shape="circle"
                                    size="xs"
                                    onClick={onClose}
                                >
                                    <IconClose fontSize={24} />
                                </IconButton>
                            </Box>
                            <Flex p={2} width="100%">
                                <Typography variant="body" weight="regular" pb={1}>
                                    {t(products.items[0].title)}
                                </Typography>
                                <Flex direction="row" alignItems="baseline">
                                    <Typography
                                        weight="bold"
                                        variant="title3"
                                        color="var(--control-primary-background)"
                                        textTransform="none"
                                    >
                                        {formatCurrency(
                                            products.items[0].priceInfo.integerPart,
                                            products.items[0].priceInfo.fractionalPart,
                                        )}
                                    </Typography>
                                    {products.items[0].oldPriceInfo.currency && (
                                        <Typography
                                            variant="body"
                                            color="var(--control-disabled-color)"
                                            textTransform="none"
                                            textDecoration="line-through"
                                            ml={1}
                                        >
                                            {formatCurrency(
                                                products.items[0].oldPriceInfo.integerPart,
                                                products.items[0].oldPriceInfo.fractionalPart,
                                            )}
                                        </Typography>
                                    )}
                                </Flex>
                                {business.siteUrl && (
                                    <Flex
                                        direction="row"
                                        justifyContent="space-between"
                                        width="100%"
                                        alignItems="center"
                                        pb={1}
                                        pt={1.5}
                                    >
                                        <Flex
                                            as="a"
                                            href={business.siteUrl}
                                            target="_blank"
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            textDecoration="none"
                                            onClick={() =>
                                                trackProductClickOut({
                                                    url: business.siteUrl,
                                                    target: 'more_products',
                                                })
                                            }
                                        >
                                            <Avatar
                                                width={34}
                                                title={business.displayName}
                                                src={business.avatarUrl}
                                                border="1px solid var(--divider)"
                                            />
                                            <Flex>
                                                <Typography>
                                                    {products.businessButtonTitle ||
                                                        t('Еще товары')}
                                                </Typography>
                                                <Typography variant="caption" color="secondary">
                                                    {business.shortSite}
                                                </Typography>
                                            </Flex>
                                        </Flex>
                                        <IconForward fontSize="16px" fill="var(--color-primary)" />
                                    </Flex>
                                )}
                                <Box pt={2} width="100%">
                                    <Button
                                        as="a"
                                        href={products.items[0].url}
                                        target="_blank"
                                        width="100%"
                                        color="primary"
                                        onClick={() =>
                                            trackProductClickOut({
                                                url: products.items[0].url,
                                                target: 'product',
                                                productId: products.items[0].id,
                                            })
                                        }
                                    >
                                        {t('Купить на сайте')}
                                    </Button>
                                </Box>
                            </Flex>
                        </DrawerContent>
                    </Drawer>
                )}
            </TransitionSlideUp>
        </SwitchTransition>
    );
};
